import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './Icon';

/**
 * Ctrl/⌘-K: jump to any page or recent dataset by typing a few letters.
 *
 * Matching is subsequence-based (the letters appear in order), ranked by how
 * early and how contiguously they match, so "mroi" finds Marketing ROI and
 * "fc" finds Forecast.
 */

export interface PaletteItem {
  id: string;
  label: string;
  group: string;
  icon: string;
  keywords?: string;
  run: () => void;
}

function score(query: string, text: string): number | null {
  if (query === '') return 0;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  const direct = t.indexOf(q);
  if (direct !== -1) return direct === 0 ? 1000 : 800 - direct;
  let ti = 0;
  let gaps = 0;
  let last = -1;
  for (const ch of q) {
    const found = t.indexOf(ch, ti);
    if (found === -1) return null;
    if (last !== -1 && found !== last + 1) gaps += 1;
    last = found;
    ti = found + 1;
  }
  return 500 - gaps * 20 - last;
}

export function CommandPalette({ items, open, onClose }: { items: PaletteItem[]; open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setIndex(0);
      window.setTimeout(() => input.current?.focus(), 0);
    }
  }, [open]);

  const results = useMemo(() => {
    return items
      .map((item) => {
        const best = Math.max(score(query, item.label) ?? -Infinity, (score(query, item.keywords ?? '') ?? -Infinity) - 100);
        return { item, s: best };
      })
      .filter((r) => r.s > -Infinity)
      .sort((a, b) => b.s - a.s)
      .slice(0, 12)
      .map((r) => r.item);
  }, [items, query]);

  useEffect(() => setIndex(0), [query]);

  useEffect(() => {
    list.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [index]);

  if (!open) return null;

  const choose = (item: PaletteItem | undefined) => {
    if (item === undefined) return;
    onClose();
    item.run();
  };

  return (
    <div className="palette-backdrop" onMouseDown={onClose}>
      <div className="palette" role="dialog" aria-modal="true" aria-label="Command palette" onMouseDown={(e) => e.stopPropagation()}>
        <div className="palette-input">
          <Icon name="search" size={16} />
          <input
            ref={input}
            value={query}
            placeholder="Jump to a page, report or dataset…"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setIndex((i) => Math.min(results.length - 1, i + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setIndex((i) => Math.max(0, i - 1));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                choose(results[index]);
              } else if (e.key === 'Escape') {
                onClose();
              }
            }}
            aria-controls="palette-results"
            aria-activedescendant={results[index] === undefined ? undefined : `pal-${results[index]!.id}`}
          />
          <kbd>Esc</kbd>
        </div>
        <ul className="palette-results" id="palette-results" role="listbox" ref={list}>
          {results.length === 0 && <li className="palette-empty">No match for “{query}”.</li>}
          {results.map((item, i) => (
            <li
              key={item.id}
              id={`pal-${item.id}`}
              role="option"
              aria-selected={i === index}
              className={i === index ? 'palette-item active' : 'palette-item'}
              onMouseEnter={() => setIndex(i)}
              onClick={() => choose(item)}
            >
              <Icon name={item.icon} size={16} />
              <span className="palette-label">{item.label}</span>
              <span className="palette-group">{item.group}</span>
            </li>
          ))}
        </ul>
        <div className="palette-foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>↵</kbd> open</span>
          <span><kbd>Ctrl</kbd><kbd>K</kbd> toggle</span>
        </div>
      </div>
    </div>
  );
}
